/**
 * ============================================================================
 * MonedaSheet — Centro de Monedas
 * ============================================================================
 * Permite al usuario definir TRM proyectadas por moneda. Vacío = usar valor
 * de mercado actual (`trmMap`). COP siempre fijo en 1.
 * ============================================================================
 */
import { useEffect, useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trmMap: Record<string, number>;
  initialOverrides: Record<string, number>;
  monedas: string[];
  onApply: (overrides: Record<string, number>) => void;
}

const fmt = (n: number) =>
  n.toLocaleString("es-CO", { maximumFractionDigits: 2 });

export function MonedaSheet({
  open,
  onOpenChange,
  trmMap,
  initialOverrides,
  monedas,
  onApply,
}: Props) {
  // Texto local por moneda; "" = usar valor de mercado.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Lista única, COP primero, resto ordenado.
  const list = useMemo(() => {
    const set = new Set<string>(["COP"]);
    for (const m of monedas) if (m) set.add(m.toUpperCase());
    // Asegurar USD/EUR como moneda relevante si están en mapa.
    for (const m of ["USD", "EUR"]) if (trmMap[m] != null) set.add(m);
    const arr = Array.from(set);
    arr.sort((a, b) => (a === "COP" ? -1 : b === "COP" ? 1 : a.localeCompare(b)));
    return arr;
  }, [monedas, trmMap]);

  useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    for (const m of list) {
      if (m === "COP") continue;
      const ov = initialOverrides[m];
      next[m] = ov != null && Number.isFinite(ov) ? String(ov) : "";
    }
    setDrafts(next);
  }, [open, initialOverrides, list]);

  const handleReset = () => {
    const next: Record<string, string> = {};
    for (const m of list) if (m !== "COP") next[m] = "";
    setDrafts(next);
  };

  const handleApply = () => {
    const out: Record<string, number> = {};
    for (const [m, raw] of Object.entries(drafts)) {
      const trimmed = (raw ?? "").trim();
      if (!trimmed) continue;
      const num = Number(trimmed);
      if (Number.isFinite(num) && num > 0) out[m] = num;
    }
    onApply(out);
    onOpenChange(false);
  };

  const overrideCount = Object.values(drafts).filter(
    (v) => (v ?? "").trim() !== "" && Number.isFinite(Number(v)) && Number(v) > 0,
  ).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-[480px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Centro de Monedas</SheetTitle>
          <SheetDescription>
            Define las TRM para proyectar costos. Vacío = usar valor de mercado.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-4 -mx-6 px-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Moneda</TableHead>
                <TableHead className="text-right w-[140px]">TRM proyectada</TableHead>
                <TableHead className="text-right">Mercado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((m) => {
                if (m === "COP") {
                  return (
                    <TableRow key="COP">
                      <TableCell className="font-mono font-medium">COP</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        $1.00
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="text-[10px]">Fijo</Badge>
                      </TableCell>
                    </TableRow>
                  );
                }
                const market = Number(trmMap[m] ?? 0);
                const draft = drafts[m] ?? "";
                return (
                  <TableRow key={m}>
                    <TableCell className="font-mono font-medium">{m}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        step="0.01"
                        value={draft}
                        placeholder={market > 0 ? fmt(market) : "—"}
                        onChange={(e) =>
                          setDrafts((prev) => ({ ...prev, [m]: e.target.value }))
                        }
                        className="h-8 w-28 text-right text-[13px] tabular-nums ml-auto"
                      />
                    </TableCell>
                    <TableCell className="text-right text-[11px] text-muted-foreground tabular-nums">
                      {market > 0 ? fmt(market) : "Sin datos"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <SheetFooter className="border-t pt-4 mt-2">
          <div className="flex w-full items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="h-3.5 w-3.5" />
              Restaurar valores de mercado
            </Button>
            <div className="flex items-center gap-2">
              {overrideCount > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  {overrideCount} {overrideCount === 1 ? "moneda modificada" : "monedas modificadas"}
                </span>
              )}
              <Button size="sm" onClick={handleApply}>
                Aplicar
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
